import { reactive, onUnmounted } from 'vue';
import { useStore } from 'vuex';

const MIN_DRAG_DISTANCE = 4; // px — evita drag acidental em cliques simples

export function useCardDayDrag({ dayCellRefs, statusColorClasses }) {
  const store = useStore();

  const dragState = reactive({
    draggingId: null,
    targetDayKey: null,
  });

  let activeDrag = null;
  let clone = null;

  function getTargetCell(clientX, clientY) {
    const cells = dayCellRefs.value;
    let result = null;
    cells.forEach((el, dayKey) => {
      if (!el || result) return;
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        result = dayKey;
      }
    });
    return result;
  }

  function createClone(card, event, cardEl) {
    const rect = cardEl.getBoundingClientRect();
    const { bar } = statusColorClasses(card);

    const el = document.createElement('div');
    el.className = [
      'fixed',
      'z-[9999]',
      'pointer-events-none',
      'rounded',
      'px-1',
      'text-xs',
      'shadow-lg',
      'opacity-90',
      'text-white',
      'truncate',
      bar,
    ].join(' ');
    el.style.width = `${rect.width}px`;
    el.style.height = `${rect.height}px`;
    el.style.left = `${event.clientX - activeDrag.offsetX}px`;
    el.style.top = `${event.clientY - activeDrag.offsetY}px`;
    el.textContent = card.title;

    document.body.appendChild(el);

    return el;
  }

  function onMousemove(event) {
    if (!activeDrag) return;

    const { startX, startY } = activeDrag;
    const moved =
      Math.abs(event.clientX - startX) > MIN_DRAG_DISTANCE ||
      Math.abs(event.clientY - startY) > MIN_DRAG_DISTANCE;

    if (!moved && !activeDrag.started) return;

    if (!activeDrag.started) {
      activeDrag.started = true;
      dragState.draggingId = activeDrag.card.id;
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      clone = createClone(activeDrag.card, event, activeDrag.cardEl);
    }

    clone.style.left = `${event.clientX - activeDrag.offsetX}px`;
    clone.style.top = `${event.clientY - activeDrag.offsetY}px`;

    const targetDayKey = getTargetCell(event.clientX, event.clientY);
    activeDrag.targetDayKey = targetDayKey;
    dragState.targetDayKey = targetDayKey;
  }

  function onMouseup() {
    if (!activeDrag) return;

    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    if (clone) {
      clone.remove();
      clone = null;
    }

    dragState.draggingId = null;
    dragState.targetDayKey = null;

    if (!activeDrag.started) {
      activeDrag = null;
      return;
    }

    const { card, targetDayKey } = activeDrag;
    activeDrag = null;

    if (!targetDayKey || targetDayKey === card.closing_date) return;

    store.dispatch('kanban/updateCard', {
      cardId: card.id,
      data: { closing_date: targetDayKey },
    });
  }

  function onCardMousedown(event, card, cardEl) {
    if (activeDrag) {
      document.removeEventListener('mousemove', onMousemove);
      document.removeEventListener('mouseup', onMouseup);
      if (clone) {
        clone.remove();
        clone = null;
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      dragState.draggingId = null;
      dragState.targetDayKey = null;
      activeDrag = null;
    }

    event.preventDefault();

    const rect = cardEl.getBoundingClientRect();
    activeDrag = {
      card,
      cardEl,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      started: false,
      targetDayKey: null,
    };

    document.addEventListener('mousemove', onMousemove);
    document.addEventListener('mouseup', onMouseup);
  }

  onUnmounted(() => {
    document.removeEventListener('mousemove', onMousemove);
    document.removeEventListener('mouseup', onMouseup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    activeDrag = null;
  });

  return { onCardMousedown, dragState };
}
